import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import APUtilResources from '@shared/utils/resources/resources.utils';

import type { IAPBrandLogoProps } from './brand-logo';
import type { IAPIconAstropayProps } from './icon-ap/types';
import type {
  APMediaImgSize,
  APMediaProps,
  APMediaTemplateRadius,
  IAPMediaBucketGlobal,
  IAPMediaFlag,
  IAPMediaImg,
} from './types';

import APIconAstropay from './icon-ap';
import defaultImgPlaceHolderDark from './image-s3/img-placeholder-dark.svg';
import defaultImgPlaceHolderWhite from './image-s3/img-placeholder.svg';
import { APMediaFallback } from './media-fallback';

const APBrandLogo = React.lazy(() => import('./brand-logo'));

const failedImages = new Set<string>();

export const APMedia: React.FC<APMediaProps> = (props) => {
  const {
    type,
    style,
    size,
    aspectRatio,
    cacheMode,
    rounded,
    objectFit,
    objectPosition,
    rootClass,
    fullWidth = false,
    inverseByTheme,
    hueImgFilter,
    subIcon,
    placeholderSettings,
    ...restProps
  } = props;

  const [imgSrc, setImgSrc] = useState<string | undefined>();
  const [placeHolderUsed, setPlaceHolderUsed] = useState(false);
  const [loadedImage, setLoadedImage] = useState(false);

  const getCacheMode = useCallback(() => {
    return '?v=' + (cacheMode !== undefined ? cacheMode : '1.0.0');
  }, [cacheMode]);

  const _subIcon = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { translateX, translateY, ...filteredSubIconProps } = subIcon || {};
    return filteredSubIconProps as APMediaProps;
  }, [subIcon]);

  // Extract specific properties to avoid restProps dependency issues
  const imageProps = useMemo(() => {
    const img = restProps as IAPMediaImg;
    const s3Img = restProps as IAPMediaBucketGlobal;
    const flagImg = restProps as IAPMediaFlag;

    return {
      imageSrc: img.src,
      s3Icon: s3Img.icon,
      s3ImgSize: s3Img.imgSize,
      s3Extension: s3Img.extension,
      s3Src: s3Img.src,
      flagName: flagImg.flag,
      flagRounded: flagImg.rounded,
      flagExtension: flagImg.extension,
      flagSrc: flagImg.src,
    };
  }, [restProps]);

  useEffect(() => {
    let newSrc: string | undefined;

    switch (type) {
      case 'image': {
        newSrc = `${imageProps.imageSrc}${getCacheMode()}`;
        break;
      }
      case 's3-bucket-img':
      case 's3-global': {
        const imgName = `${imageProps.s3Icon}${imageProps.s3ImgSize ? imageProps.s3ImgSize : ''}`;
        const srcImg = APUtilResources.buildImageUrl(
          'images',
          imgName,
          imageProps.s3Extension !== undefined ? imageProps.s3Extension : 'svg',
          imageProps.s3Src
        );
        newSrc = `${srcImg}${getCacheMode()}`;
        break;
      }
      case 'flag-country':
      case 'flag-currency': {
        const imgName = `${imageProps.flagName}${imageProps.flagRounded ? '_circle' : ''}`;
        const srcImg = APUtilResources.buildImageUrl(
          type === 'flag-country' ? 'countryFlags' : 'currencyFlags',
          imgName,
          imageProps.flagExtension !== undefined ? imageProps.flagExtension : 'webp',
          imageProps.flagSrc
        );
        newSrc = `${srcImg}${getCacheMode()}`;
        break;
      }
    }

    // Only update if we have a new src and it's different from current
    if (newSrc && newSrc !== imgSrc) {
      // Reset state when changing image sources
      setPlaceHolderUsed(false);
      setLoadedImage(false);

      // If this is a genuinely new image (not previously failed), set it
      if (!failedImages.has(newSrc)) {
        setImgSrc(newSrc);
      } else {
        // If the new image was previously failed, go straight to placeholder
        setPlaceHolderUsed(true);
      }
    }
  }, [type, imageProps, getCacheMode, imgSrc]);

  const _aspectRatio = useMemo(() => {
    if (aspectRatio === undefined) {
      if (type === 'flag-country' || type === 'flag-currency') {
        return '4 / 3';
      }
    }
    return aspectRatio;
  }, [aspectRatio, type]);

  const _iconSize = useMemo(() => {
    if (restProps.template?.iconSize) {
      return restProps.template.iconSize;
    }

    if (restProps.template) {
      switch (size) {
        case 'ultraLarge':
          return 45;
        case 'large':
          return 24;
        case 'medium':
          return 20;
        case 'base':
          return 20;
        case 'small':
          return 18;
        case 'smaller':
          return 16;
        default:
          if (size) {
            return size * 0.6;
          } else return 40;
      }
    }

    if (size === undefined) {
      if (type === 'image') {
        return 24;
      }
      if (type === 'flag-country' || type === 'flag-currency') {
        return 24;
      }
    }
    const isImg: boolean =
      type === 'image' ||
      type === 's3-global' ||
      type === 'flag-currency' ||
      type === 'flag-country' ||
      type === 's3-bucket-img';

    if (isImg && typeof size === 'string') {
      switch (size as APMediaImgSize) {
        case 'ultraLarge':
          return 512;
        case 'large':
          return 256;
        case 'medium':
          return 128;
        case 'base':
          return 64;
        case 'small':
          return 32;
        case 'smaller':
          return 16;
        case 'none':
          break;
        default:
          return 24;
      }
    }

    return size;
  }, [size, type, restProps.template]);

  const _containerSize = useMemo(() => {
    if (restProps.template) {
      if (restProps.template.containerSize) {
        return restProps.template.containerSize;
      }

      switch (size) {
        case 'ultraLarge':
          return 100;
        case 'large':
          return 56;
        case 'medium':
          return 48;
        case 'base':
          return 40;
        case 'small':
          return 32;
        case 'smaller':
          return 24;
        default:
          if (size) {
            return size;
          } else return 40;
      }
    } else {
      if (size === undefined) {
        if (type === 'image') {
          return 24;
        }
        if (type === 'flag-country' || type === 'flag-currency') {
          return 24;
        }
      } else return size;
    }
  }, [size, type, restProps.template]);

  const borderRadiusMap = {
    ultraLarge: {
      circle: 90,
      square: 8,
    },
    large: {
      circle: 32,
      square: 8,
    },
    medium: {
      circle: 24,
      square: 8,
    },
    base: {
      circle: 24,
      square: 4,
    },
    small: {
      circle: 16,
      square: 4,
    },
    smaller: {
      circle: 8,
      square: 4,
    },
  };

  const getBorderRadius = (variant: APMediaTemplateRadius) => {
    if (typeof variant === 'number' || typeof size === 'number' || size === undefined) {
      if (size === undefined) {
        return 500;
      } else {
        return variant;
      }
    } else {
      const sizeValues = borderRadiusMap[size];
      if (sizeValues) {
        return sizeValues[variant];
      } else if (variant === 'circle') {
        return 500;
      }
    }
  };

  const renderPlaceHolder = () => {
    if (placeHolderUsed) {
      let placeholder = (restProps as IAPMediaImg).placeholder;
      if (placeholder === undefined) {
        placeholder =
          placeholderSettings?.defaultPlaceHolderMode === 'Dark'
            ? defaultImgPlaceHolderDark
            : defaultImgPlaceHolderWhite;
      }

      let placeholderSize = placeholderSettings?.placeholderSize;
      if (placeholderSize === undefined) {
        placeholderSize = fullWidth ? '100%' : 30;
      }

      return (
        <div
          style={{
            display: 'flex',
            width: placeholderSize,
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
          className="place-holder-container"
        >
          {placeholderSettings?.placeholderIcon ? (
            <APIconAstropay {...placeholderSettings?.placeholderIcon} />
          ) : (
            <img
              style={{
                width: placeholderSize,
                overflow: 'hidden',
              }}
              src={placeholder}
              className="place-holder-image"
            />
          )}
        </div>
      );
    }
  };

  const renderMedia = () => {
    let placeholder = (restProps as IAPMediaImg).placeholder;

    if (placeholder === undefined) {
      placeholder =
        placeholderSettings?.defaultPlaceHolderMode === 'Dark'
          ? defaultImgPlaceHolderDark
          : defaultImgPlaceHolderWhite;
    }

    if (imgSrc && failedImages.has(imgSrc)) {
      setImgSrc(placeholder);
      setPlaceHolderUsed(true);
      return;
    }

    switch (type) {
      case 'image': {
        return (
          <img
            style={{
              width: fullWidth ? '100%' : _iconSize,
              aspectRatio: _aspectRatio,
              objectFit: objectFit,
              objectPosition: objectPosition,
              overflow: 'hidden',
              filter:
                inverseByTheme === true
                  ? 'invert(1)'
                  : hueImgFilter
                    ? 'hue-rotate(15deg)'
                    : undefined,
              pointerEvents: 'none',
              opacity: loadedImage ? 1 : 0,
            }}
            onLoad={() => {
              setLoadedImage(true);
            }}
            onError={() => {
              if (import.meta.env.MODE !== 'production') {
                console.error('image error', imgSrc);
              }
              if (imgSrc) {
                failedImages.add(imgSrc);
              }
              // Only set placeholder if it's different from current src and hasn't failed before
              if (placeholder && placeholder !== imgSrc && !failedImages.has(placeholder)) {
                setImgSrc(placeholder);
              }
              setPlaceHolderUsed(true);
            }}
            loading={(restProps as IAPMediaImg).loading}
            {...(restProps as React.ImgHTMLAttributes<HTMLImageElement>)}
            src={imgSrc}
          />
        );
      }
      case 's3-bucket-img':
      case 's3-global': {
        return (
          <img
            style={{
              width: fullWidth ? '100%' : _iconSize,
              aspectRatio: _aspectRatio,
              objectFit: objectFit,
              objectPosition: objectPosition,
              overflow: 'hidden',
              filter: inverseByTheme === true ? 'invert(1)' : undefined,
              pointerEvents: 'none',
              opacity: loadedImage ? 1 : 0,
            }}
            loading={(restProps as IAPMediaImg).loading}
            onLoad={() => {
              setLoadedImage(true);
            }}
            onError={() => {
              if (import.meta.env.MODE !== 'production') {
                console.error('image error', imgSrc);
              }
              if (imgSrc) {
                failedImages.add(imgSrc);
              }
              // Only set placeholder if it's different from current src and hasn't failed before
              if (placeholder && placeholder !== imgSrc && !failedImages.has(placeholder)) {
                setImgSrc(placeholder);
              }
              setPlaceHolderUsed(true);
            }}
            {...(restProps as React.ImgHTMLAttributes<HTMLImageElement>)}
            src={imgSrc}
          />
        );
      }
      case 'flag-country':
      case 'flag-currency':
        return (
          <img
            style={{
              width: fullWidth ? '100%' : _iconSize,
              aspectRatio: rounded ? '1/1' : _aspectRatio,
              objectFit: objectFit,
              objectPosition: objectPosition,
              overflow: 'hidden',
              pointerEvents: 'none',
              opacity: loadedImage ? 1 : 0,
            }}
            loading={(restProps as IAPMediaImg).loading}
            onLoad={() => {
              setLoadedImage(true);
            }}
            onError={() => {
              if (import.meta.env.MODE !== 'production') {
                console.error('image error', imgSrc);
              }
              if (imgSrc) {
                failedImages.add(imgSrc);
              }
              // Only set placeholder if it's different from current src and hasn't failed before
              if (placeholder && placeholder !== imgSrc && !failedImages.has(placeholder)) {
                setImgSrc(placeholder);
              }
              setPlaceHolderUsed(true);
            }}
            {...(restProps as React.ImgHTMLAttributes<HTMLImageElement>)}
            src={imgSrc}
          />
        );

      default:
        return null;
    }
  };

  if (type === 'brand-logo') {
    return (
      <APMediaFallback {...props} rootClass={rootClass} style={{ ...style }} useMainDiv={false}>
        <APBrandLogo {...(props as IAPBrandLogoProps)} size={_iconSize} />
      </APMediaFallback>
    );
  }

  if (type === 'icon-ap') {
    const iconAPProp = {
      ...props,
    };

    if (iconAPProp.rounded) {
      delete iconAPProp.rounded;
    }

    const styles = restProps.template
      ? {
          ...style,
          width: _containerSize,
          height: _containerSize,
          borderRadius: getBorderRadius(restProps.template?.radius),
        }
      : { ...style };

    return (
      <APMediaFallback
        {...props}
        rootClass={clsx(rootClass, restProps.template?.variant)}
        style={{ ...styles }}
        useMainDiv={restProps.template ? true : false}
        size={_containerSize}
      >
        {subIcon && (
          <div>
            <div
              className={'sub-icon'}
              style={{ transform: `translate(${subIcon.translateX}px, ${subIcon.translateY}px)` }}
            >
              <APMedia {..._subIcon} />
            </div>
          </div>
        )}
        <APIconAstropay {...(iconAPProp as IAPIconAstropayProps)} size={_iconSize} />
      </APMediaFallback>
    );
  }

  return (
    <APMediaFallback {...props} rootClass={rootClass} style={{ ...style }}>
      {subIcon && (
        <div>
          <div
            className={'sub-icon'}
            style={{ transform: `translate(${subIcon.translateX}px, ${subIcon.translateY}px)` }}
          >
            <APMedia {..._subIcon} />
          </div>
        </div>
      )}
      {placeHolderUsed && placeholderSettings !== undefined ? renderPlaceHolder() : renderMedia()}
    </APMediaFallback>
  );
};

export default APMedia;
